/**
 * @file date-picker/subcomponents/day-cell.tsx
 *
 * Day cell in calendar grid with complete MD3 states.
 * Tokens from DatePickerModalTokens.kt:
 * - DateContainerShape = CornerFull (pill)
 * - DateContainerHeight/Width = 40px
 * - DateSelectedContainerColor = Primary
 * - DateTodayContainerOutlineColor = Primary
 * - RangeSelectionActiveIndicatorContainerColor = SecondaryContainer
 */

import { m } from "motion/react";
import * as React from "react";
import { cn } from "../../../../lib/utils";
import { DAY_CELL_STATE_TRANSITION } from "../../shared/animation-tokens";
import {
	isDateInRange,
	isRangeEnd,
	isRangeStart,
	isSameDay,
} from "../../shared/calendar-model";
import type { CalendarDay } from "../../shared/types";

interface DayCellProps {
	day: CalendarDay;
	selectedMs: number | null;
	selectedStartMs?: number | null;
	selectedEndMs?: number | null;
	isRangePicker?: boolean;
	isSelectable: boolean;
	onSelect(ms: number): void;
	locale?: string;
}

/**
 * Day cell with 6 visual MD3 states:
 * - default, today, selected, in-range, range-start/end, disabled
 */

export const DayCell: React.FC<DayCellProps> = ({
	day,
	selectedMs,
	selectedStartMs = null,
	selectedEndMs = null,
	isRangePicker = false,
	isSelectable,
	onSelect,
	locale,
}) => {
	const { utcMs, day: dayNum, isToday } = day;

	// ── State computation ───────────────────────────────────────────────────────
	const isSelected =
		!isRangePicker && selectedMs !== null && isSameDay(utcMs, selectedMs);
	const isStart = isRangePicker && isRangeStart(utcMs, selectedStartMs);
	const isEnd = isRangePicker && isRangeEnd(utcMs, selectedEndMs);
	const isInRange =
		isRangePicker &&
		!isStart &&
		!isEnd &&
		isDateInRange(utcMs, selectedStartMs, selectedEndMs);
	const isDisabled = !isSelectable;

	// ── Accessible label ────────────────────────────────────────────────────────
	const ariaLabel = new Intl.DateTimeFormat(locale, {
		year: "numeric",
		month: "long",
		day: "numeric",
		timeZone: "UTC",
	}).format(new Date(utcMs));

	// ── Range half-fill classes ─────────────────────────────────────────────────
	// Range start → fill right half; Range end → fill left half; In range → full fill
	const rangeHighlightClass = cn(
		"absolute inset-y-0 bg-m3-secondary-container",
		isStart && selectedEndMs !== null && "left-1/2 right-0",
		isEnd && selectedStartMs !== null && "left-0 right-1/2",
		isInRange && "inset-x-0",
	);

	// ── Day circle classes ──────────────────────────────────────────────────────
	const circleClass = cn(
		"relative z-10 flex items-center justify-center w-10 h-10 rounded-m3-full",
		"text-[length:var(--md-typescale-body-large-size,14px)]",
		"font-[number:var(--md-typescale-body-large-weight,400)]",
		"transition-m3-fast-effects",
		// Selected (single) or range endpoints
		(isSelected || isStart || isEnd) && ["bg-m3-primary", "text-m3-on-primary"],
		// In-range text
		isInRange && "text-m3-on-secondary-container",
		// Today outline (only when not selected)
		isToday &&
			!isSelected &&
			!isStart &&
			!isEnd && ["outline outline-1 outline-m3-primary", "text-m3-primary"],
		// Default text
		!isSelected &&
			!isStart &&
			!isEnd &&
			!isInRange &&
			!isToday &&
			"text-m3-on-surface",
		// Hover states (only when selectable and not selected/range endpoints)
		!isDisabled &&
			!isSelected &&
			!isStart &&
			!isEnd && [
				isInRange
					? "hover:bg-m3-on-secondary-container/8"
					: "hover:bg-m3-on-surface/8",
			],
		// Disabled
		isDisabled && "opacity-[0.38]",
	);

	const handleClick = React.useCallback(() => {
		if (isDisabled) return;
		onSelect(utcMs);
	}, [isDisabled, utcMs, onSelect]);

	const handleKeyDown = React.useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				handleClick();
			}
		},
		[handleClick],
	);

	return (
		<td className="relative flex items-center justify-center">
			{/* Range highlight background (full row width) */}
			{isRangePicker && (isInRange || isStart || isEnd) && (
				<div className={rangeHighlightClass} />
			)}

			{/* Day circle */}
			<m.button
				type="button"
				className={cn(
					circleClass,
					isDisabled && "pointer-events-none cursor-not-allowed",
				)}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				aria-label={ariaLabel}
				aria-selected={isSelected || isStart || isEnd || undefined}
				aria-disabled={isDisabled || undefined}
				tabIndex={isDisabled ? -1 : 0}
				whileTap={
					!isDisabled
						? {
								scale: 0.94,
								transition: DAY_CELL_STATE_TRANSITION,
							}
						: undefined
				}
			>
				{dayNum}
			</m.button>
		</td>
	);
};

DayCell.displayName = "DayCell";
